import { useCallback, useRef, useState } from 'react';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  fetchGuestMeetingTarget,
  isGuestTargetFresh,
  navigateToMeetingSameTab,
  openMeetingPlaceholderWindow,
  openMeetingWindow,
  type CachedGuestMeetingTarget,
} from '@/utils/guest-meeting-join-utils';

/** Join a remote visio: prefetch URL, open tab, retry if the popup is blocked. */
export function useJoinRemoteMeeting() {
  const [isJoining, setIsJoining] = useState(false);
  const [blockedSessionId, setBlockedSessionId] = useState<string | null>(null);
  const targetRef = useRef<CachedGuestMeetingTarget | null>(null);

  const resolveTarget = useCallback(
    async (sessionId: string): Promise<CachedGuestMeetingTarget> => {
      const cached = targetRef.current;
      if (isGuestTargetFresh(cached, sessionId)) return cached;
      const target = { ...(await fetchGuestMeetingTarget(sessionId)), sessionId };
      targetRef.current = target;
      return target;
    },
    [],
  );

  // Must run in the click handler so window.open stays synchronous.
  const join = useCallback(
    async (sessionId: string) => {
      const placeholder = openMeetingPlaceholderWindow();
      if (placeholder.kind === 'blocked') {
        setBlockedSessionId(sessionId);
        // Prefetch so Retry can open the tab without any await.
        void resolveTarget(sessionId).catch(() => undefined);
        return;
      }

      setIsJoining(true);
      try {
        const target = await resolveTarget(sessionId);
        placeholder.assign(target.url);
        setBlockedSessionId(null);
      } catch (error) {
        placeholder.close();
        console.error('Failed to join meeting:', error);
        toast.error(getErrorMessage(error));
      } finally {
        setIsJoining(false);
      }
    },
    [resolveTarget],
  );

  const handleClose = useCallback(() => {
    setBlockedSessionId(null);
  }, []);

  const handleRetry = useCallback(() => {
    if (!blockedSessionId) return;
    const cached = targetRef.current;
    if (isGuestTargetFresh(cached, blockedSessionId)) {
      if (openMeetingWindow(cached.url)) setBlockedSessionId(null);
      return;
    }
    void join(blockedSessionId);
  }, [blockedSessionId, join]);

  const handleOpenSameTab = useCallback(() => {
    if (!blockedSessionId) return;
    void resolveTarget(blockedSessionId)
      .then((target) => navigateToMeetingSameTab(target.url))
      .catch((error) => {
        console.error('Failed to join meeting:', error);
        toast.error(getErrorMessage(error));
      });
  }, [blockedSessionId, resolveTarget]);

  return {
    isJoining,
    join,
    popupBlocked: {
      open: blockedSessionId !== null,
      onClose: handleClose,
      onRetry: handleRetry,
      onOpenSameTab: handleOpenSameTab,
    },
  };
}
